import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { X } from 'lucide-react';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './button';

const overlayClass =
  'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out';
const contentClass =
  'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-surface-raised) p-4 shadow-lg max-h-[calc(100dvh-2rem)] overflow-y-auto sm:w-[calc(100vw-2rem)] sm:p-6';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogContent({
  className,
  children,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlayClass} />
      <DialogPrimitive.Content className={cn(contentClass, className)} {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute right-2 top-2 flex size-11 items-center justify-center rounded-md text-(--color-content-muted) hover:bg-(--color-surface-muted) md:right-4 md:top-4 md:size-8"
          aria-label="Fechar"
        >
          <X className="size-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('mb-4 flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      // `flex-wrap` e `min-w-0`: o rodapé é uma linha só até não caber mais. Sem eles, um
      // item que carrega texto — o aviso embaixo do botão de confirmar — não encolhe abaixo
      // do conteúdo (`min-width: auto` é o padrão do flex) e empurra a linha para fora da
      // moldura do diálogo, com o botão escapando pela borda direita.
      className={cn(
        'mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end [&>*]:min-w-0',
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={cn('text-lg font-semibold', className)} {...props} />;
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-sm text-(--color-content-muted)', className)}
      {...props}
    />
  );
}

// ── Confirmação de ação destrutiva ────────────────────────────────────────────

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * Trava o botão de confirmar.
   *
   * Existe para a operação que exige um passo antes — restaurar backup só depois de o
   * usuário garantir que tem cópia dos dados atuais. Sem isso, o diálogo teria de virar
   * um componente próprio só para poder desabilitar um botão.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  children?: React.ReactNode;
}

/**
 * Toda ação destrutiva passa por aqui.
 *
 * `description` deve dizer **o que exatamente** acontece — não existe servidor para
 * desfazer, então o usuário precisa saber antes de confirmar.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  confirmDisabled = false,
  onConfirm,
  children,
}: ConfirmDialogProps) {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className={overlayClass} />
        <AlertDialogPrimitive.Content className={contentClass}>
          <AlertDialogPrimitive.Title className="text-lg font-semibold">
            {title}
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description className="mt-1.5 text-sm text-(--color-content-muted)">
            {description}
          </AlertDialogPrimitive.Description>
          {children}
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="secondary">{cancelLabel}</Button>
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action asChild disabled={confirmDisabled}>
              <Button
                variant={destructive ? 'danger' : 'primary'}
                onClick={onConfirm}
                disabled={confirmDisabled}
              >
                {confirmLabel}
              </Button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
